// sketchCopy — зеркало и массив примитивов эскиза.
//
// Нужны для симметричных корпусов и повторяющихся отверстий: половину
// контура рисуют один раз и отражают, ряд крепёжных отверстий задают одним
// массивом. Рисовать каждый элемент руками — долго и неточно.
//
// Обе операции работают ОДИНАКОВО: копируют выбранные примитивы, пересчитывая
// координаты точек. Отличается только преобразование.

/**
 * Отражение точки относительно прямой, заданной точкой и направлением.
 */
function mirrorPoint(pt, origin, dir) {
    // Вектор от оси к точке.
    const vx = pt.x - origin.x;
    const vy = pt.y - origin.y;
    // Проекция на направление оси.
    const d = vx * dir.x + vy * dir.y;
    const px = dir.x * d;
    const py = dir.y * d;
    // Отражённая точка: дважды проекция минус исходный вектор.
    return {
        x: origin.x + 2 * px - vx,
        y: origin.y + 2 * py - vy,
    };
}

/**
 * Скопировать примитивы эскиза с преобразованием точек.
 *
 * prims     — все примитивы эскиза;
 * ids       — какие копировать;
 * transform — (точка) => новая точка;
 * newId     — генератор идентификаторов;
 * flipArc   — менять ли направление обхода дуг (для зеркала — да).
 *
 * Возвращает массив новых примитивов, готовых к добавлению.
 */
export function copyPrimitives(prims, ids, transform, newId, flipArc = false) {
    const byId = new Map(prims.map((p) => [p.id, p]));
    const chosen = ids.map((id) => byId.get(id)).filter(Boolean);
    if (chosen.length === 0) return [];

    /*
     * Собираем ВСЕ точки, на которые ссылаются выбранные линии и дуги, —
     * даже если сама точка не выделена. Иначе копия линии ссылалась бы на
     * оригинальные концы, и обе линии тянулись бы за одну точку.
     */
    const needPoints = new Set();
    for (const p of chosen) {
        if (p.kind === 'point') needPoints.add(p.id);
        if (p.p1) needPoints.add(p.p1);
        if (p.p2) needPoints.add(p.p2);
    }

    const out = [];
    const pointMap = new Map();   // старый id точки → новый

    for (const oldId of needPoints) {
        const src = byId.get(oldId);
        if (!src || src.kind !== 'point') continue;
        const np = transform({ x: src.x, y: src.y });
        const nid = newId('p');
        pointMap.set(oldId, nid);
        out.push({ kind: 'point', id: nid, x: np.x, y: np.y });
    }

    for (const p of chosen) {
        if (p.kind === 'point') continue;   // уже скопирована выше
        if (p.kind === 'line') {
            out.push({
                kind: 'line',
                id: newId('l'),
                p1: pointMap.get(p.p1) || p.p1,
                p2: pointMap.get(p.p2) || p.p2,
            });
        } else if (p.kind === 'circle') {
            const c = transform({ x: p.cx, y: p.cy });
            out.push({ kind: 'circle', id: newId('c'), cx: c.x, cy: c.y, r: p.r });
        } else if (p.kind === 'arc') {
            const c = transform({ x: p.cx, y: p.cy });
            out.push({
                kind: 'arc',
                id: newId('a'),
                p1: pointMap.get(p.p1) || p.p1,
                p2: pointMap.get(p.p2) || p.p2,
                cx: c.x,
                cy: c.y,
                // 🔴 При отражении обход дуги меняется на противоположный:
                // зеркало переворачивает ориентацию плоскости. Без этого
                // дуга «выворачивается» наизнанку.
                ccw: flipArc ? !p.ccw : p.ccw,
            });
        }
    }
    return out;
}

/**
 * Зеркало относительно оси.
 * axis — 'x' | 'y' (горизонтальная или вертикальная ось эскиза).
 */
export function mirrorPrimitives(prims, ids, axis, newId) {
    const origin = { x: 0, y: 0 };
    // Ось X — отражаем по горизонтали (меняется знак Y), и наоборот.
    const dir = axis === 'x' ? { x: 1, y: 0 } : { x: 0, y: 1 };
    return copyPrimitives(
        prims, ids,
        (pt) => mirrorPoint(pt, origin, dir),
        newId,
        true,
    );
}

/**
 * Прямоугольный массив: count копий с шагом (dx, dy).
 * Исходные примитивы остаются, добавляются count−1 копий.
 */
export function patternPrimitives(prims, ids, count, dx, dy, newId) {
    const n = Math.max(2, Math.floor(count));
    const out = [];
    for (let i = 1; i < n; i++) {
        out.push(...copyPrimitives(
            prims, ids,
            (pt) => ({ x: pt.x + dx * i, y: pt.y + dy * i }),
            newId,
        ));
    }
    return out;
}
